import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import studentApplicationService from '@/shared/services/studentApplicationService';
import { formatFileSize, formatLongDate } from './shared';

export function ResumeDownload({ fetchResume, resume }) {
    const [failed, setFailed] = useState(false);

    const download = async () => {
        setFailed(false);
        try {
            const blob = await fetchResume();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = resume.fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(url), 0);
        } catch {
            setFailed(true);
        }
    };

    return (
        <>
            <Button variant="link" className="p-0" onClick={download}>
                Download resume
            </Button>
            <div role="status" className="small">
                {failed && 'The resume could not be downloaded. Try again.'}
            </div>
        </>
    );
}

/**
 * The resume's name and size, with a download. `application` is the server's
 * shape, or one built from the wizard's answers. `fetchResume` is how the
 * reader downloads it (faculty download through the candidacy); without one the
 * application's own id is used, and with neither there is no download.
 * Every reader sees the same name: the server decides who is sent a resume.
 */
export default function ResumeAnswer({ application, fetchResume }) {
    const resume = application.resume;
    const applicationId = application._id || application.id;
    const resumeFetcher =
        fetchResume ||
        (applicationId
            ? () => studentApplicationService.downloadResume(applicationId)
            : null);
    if (!resume) return 'None attached';
    return (
        <>
            {resume.fileName} ({formatFileSize(resume.size)})
            {resume.updatedAt && (
                <div className="small text-muted">
                    Resume updated by the student on{' '}
                    {formatLongDate(resume.updatedAt)}
                </div>
            )}
            {application.resumeHistory?.length > 0 && (
                <div className="small text-muted">
                    Earlier versions sent:
                    <ul className="mb-0">
                        {application.resumeHistory.map((earlier) => (
                            <li
                                key={`${earlier.fileName}-${earlier.replacedAt}`}
                            >
                                {earlier.fileName} (
                                {formatFileSize(earlier.size)}), replaced{' '}
                                {formatLongDate(earlier.replacedAt)}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {resumeFetcher && (
                <div>
                    <ResumeDownload
                        fetchResume={resumeFetcher}
                        resume={resume}
                    />
                </div>
            )}
        </>
    );
}
